import React, { useContext, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Mail, Lock, ArrowRight, CheckCircle2, ShieldCheck, Store } from 'lucide-react';
import UniversalNavbar from '../components/UniversalNavbar';
import AuthContext from '../context/AuthContext';
import { PrimaryButton } from '../atelier';
// Live store snapshots reused from the theme gallery (scroll on hover).
import { SNAP as snapUrl } from '../utils/brandAssets';
import { firstJobRoute, needsSetup } from '../utils/businessConfig';
const SHOWCASE = [
  { id: 16, name: 'VIVAHA', tag: 'Editorial & elegant' },
  { id: 9, name: 'ZEVAR', tag: 'Minimal & refined' },
  { id: 18, name: 'KINETIC', tag: 'Bold & modern' },
];

// How many base designs ship with the product. Named rather than typed into a
// sentence because that is exactly how it went stale: the copy still claimed 27
// long after the count reached 32 (audit L-19). Ground truth is the file count
// in `static/template_bases/template_*.json`, which the `temp_no` ceiling in
// `products/views/template_theme_views.py` is bumped alongside. Update all
// three together when a design is added — see CREATE_NEW_TEMPLATE.md.
const BASE_DESIGN_COUNT = 32;

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { loginUser, user } = useContext(AuthContext);
  const [searchParams] = useSearchParams();

  if (user) {
    // If kicked here via a 401 interceptor with ?redirect=<orig path>, restore
    // the user there post-login. Only same-origin paths (open-redirect guard).
    const redirect = searchParams.get('redirect');
    const safeRedirect =
      redirect && redirect.startsWith('/') && !redirect.startsWith('//')
        ? redirect
        // A brand-new business that hasn't picked its features yet is sent to
        // the setup page, not the dashboard.
        : needsSetup() ? '/setup'
        // no explicit redirect → the business's chosen first job (fashion = /homepage).
        : firstJobRoute();
    return <Navigate to={safeRedirect} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await loginUser(e);
    } catch (err) {
      setError('Invalid email or password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Shopfront form language — white input, 1.5px border, crimson focus ring.
  const fieldWrap =
    'flex items-center gap-3 rounded-[10px] border-[1.5px] border-gray-300 bg-white px-4 h-[52px] transition-colors focus-within:border-ink focus-within:ring-2 focus-within:ring-[#B40A0A]';
  const inputCls = 'w-full bg-transparent text-[15px] text-ink outline-none placeholder:text-gray-400';
  const labelCls = 'block font-display text-[11px] font-bold uppercase tracking-[0.14em] text-ink';

  return (
    <div className="min-h-screen overflow-x-hidden bg-bone text-ink">
      <UniversalNavbar />

      <div className="mx-auto grid min-h-screen max-w-7xl grid-cols-1 items-stretch gap-0 px-4 pt-16 sm:px-6 lg:grid-cols-2 lg:gap-12 lg:px-8">
        {/* ───────────── Left: form ───────────── */}
        <div className="flex items-center justify-center py-12 lg:py-0">
          <motion.div
            className="w-full max-w-md"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-primary">
              The business portal
            </p>
            <h1 className="mt-3 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-ink sm:text-[2.7rem]">
              Welcome back to your dashboard.
            </h1>
            <p className="mt-3 text-[15.5px] text-muted">
              Manage products, orders and analytics — all in one dashboard.
            </p>

            {/* The sign-in window — Shopfront signature card, THE pop of this view */}
            <div className="win-card win-pop mt-8">
              <div className="win-bar">
                <span className="win-dot win-dot--red" />
                <span className="win-dot win-dot--ink" />
                <span className="win-dot win-dot--cream" />
                <span className="ml-2 truncate font-display text-[10.5px] font-bold uppercase tracking-[0.14em] text-muted">
                  inikru.com — sign in
                </span>
                <span className="badge-open ml-auto flex-shrink-0">Open</span>
              </div>

              <div className="p-6 sm:p-8">
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-5 rounded-[10px] border-[1.5px] border-red-300 bg-red-50 px-4 py-3 text-[13.5px] font-semibold text-red-700"
                    role="alert"
                  >
                    {error}
                  </motion.div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Email */}
                  <div>
                    <label htmlFor="email" className={`mb-1.5 ${labelCls}`}>
                      Email address
                    </label>
                    <div className={fieldWrap}>
                      <Mail size={18} className="flex-shrink-0 text-muted" />
                      <input
                        type="email"
                        name="email"
                        id="email"
                        placeholder="you@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        autoComplete="email"
                        className={inputCls}
                      />
                    </div>
                  </div>

                  {/* Password */}
                  <div>
                    <div className="mb-1.5 flex items-center justify-between">
                      <label htmlFor="password" className={labelCls}>
                        Password
                      </label>
                      <Link to="/forgot-password" className="text-[13px] font-semibold text-primary hover:text-primary-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B40A0A] rounded">
                        Forgot?
                      </Link>
                    </div>
                    <div className={fieldWrap}>
                      <Lock size={18} className="flex-shrink-0 text-muted" />
                      <input
                        type={passwordVisible ? 'text' : 'password'}
                        name="password"
                        id="password"
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        autoComplete="current-password"
                        className={inputCls}
                      />
                      <button
                        type="button"
                        onClick={() => setPasswordVisible((v) => !v)}
                        className="rounded text-gray-400 transition-colors hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B40A0A]"
                        aria-label={passwordVisible ? 'Hide password' : 'Show password'}
                      >
                        {passwordVisible ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  <label className="flex cursor-pointer items-center gap-2 text-[13.5px] font-semibold text-muted">
                    <input type="checkbox" name="remember-me" className="h-4 w-4 rounded accent-[#B40A0A]" />
                    Keep me signed in
                  </label>

                  {/* Submit — THE crimson on this screen */}
                  <PrimaryButton type="submit" disabled={isLoading} className="w-full">
                    {isLoading ? (
                      <>
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                        Signing in…
                      </>
                    ) : (
                      <>
                        Sign in <ArrowRight className="h-5 w-5" />
                      </>
                    )}
                  </PrimaryButton>
                </form>

                <div className="my-6 flex items-center gap-3">
                  <span className="h-px flex-1 bg-line" />
                  <span className="text-[12.5px] font-semibold text-muted">New to Inikru?</span>
                  <span className="h-px flex-1 bg-line" />
                </div>

                <Link
                  to="/Register"
                  className="flex w-full items-center justify-center gap-2 rounded-[10px] border-[1.5px] border-gray-300 bg-white px-6 py-3.5 font-display text-[14.5px] font-bold text-ink transition-colors hover:border-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B40A0A]"
                >
                  Open your store free
                </Link>
              </div>
            </div>

            <p className="mt-6 text-center text-[13px] text-muted">
              Need help?{' '}
              <a href="mailto:team@inikru.com" className="font-semibold text-primary hover:text-primary-dark">
                team@inikru.com
              </a>{' '}
              · +91 97043 87878
            </p>
          </motion.div>
        </div>

        {/* ───────────── Right: brand showcase (desktop) ───────────── */}
        <div className="relative hidden lg:flex">
          <div className="relative my-10 flex w-full items-center overflow-hidden rounded-[12px] bg-ink">
            <Store aria-hidden className="pointer-events-none absolute -right-10 -top-8 h-56 w-56 text-white opacity-5" />

            <div className="relative z-10 w-full px-12 py-14">
              <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-white/60">
                <ShieldCheck className="h-4 w-4" /> Your store, your customers, your revenue
              </p>
              <h2 className="mt-5 max-w-md font-display text-[2.5rem] font-extrabold leading-[1.08] tracking-tight text-white">
                Pick up right where you left off.
              </h2>
              <p className="mt-4 max-w-md text-[15.5px] leading-relaxed text-gray-400">
                {BASE_DESIGN_COUNT} beautiful designs, built-in payments, and everything you need to run your brand online.
              </p>

              <div className="mt-9 grid grid-cols-3 gap-4">
                {SHOWCASE.map((t) => (
                  <div key={t.id} className="group overflow-hidden rounded-[10px] border-[1.5px] border-white/15 bg-white/5">
                    <div
                      className="h-40 w-full bg-no-repeat bg-top transition-[background-position] duration-[5000ms] ease-linear group-hover:bg-bottom"
                      style={{ backgroundImage: `url(${snapUrl(t.id)})`, backgroundSize: '100% auto' }}
                      aria-label={`${t.name} store design`}
                    />
                    <div className="border-t border-white/10 px-3 py-2.5">
                      <span className="block font-display text-[13px] font-bold tracking-tight text-white">{t.name}</span>
                      <span className="text-[11px] font-semibold text-gray-500">{t.tag}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-9 flex flex-wrap gap-x-6 gap-y-2.5">
                {['Keep 97% of every sale', 'Live in under 30 minutes', 'No marketplace commission'].map((t) => (
                  <span key={t} className="flex items-center gap-1.5 text-[13.5px] font-semibold text-white">
                    <CheckCircle2 className="h-4 w-4 text-gray-400" />
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
